import React from 'react';
import ComplianceCalendar, { type CalendarListRow, type PortfolioCalendarEvent } from './ComplianceCalendar';
import {
  COMPLIANCE_CONSOLES,
  loadComplianceConsoleState,
} from '../constants/complianceChecklists';
import { calendarSuggestionsFor, frequencyLabel, parseCalendarEvents } from '../complianceCalendar';
import { PUBLISHED_DEADLINES } from '../publishedDeadlines';

type PortfolioAccount = {
  company_id: number;
  company_name: string;
};

type CompliancePortfolioPageProps = {
  language: 'en' | 'es';
  accounts: PortfolioAccount[];
  apiBase: string;
  accessToken: string;
};

const CompliancePortfolioPage: React.FC<CompliancePortfolioPageProps> = ({
  language,
  accounts,
  apiBase,
  accessToken,
}) => {
  const [portfolio, setPortfolio] = React.useState<PortfolioCalendarEvent[]>([]);
  const [savedByKey, setSavedByKey] = React.useState<Record<string, { date: string; skipped?: boolean }>>({});
  const accountKey = accounts.map((account) => `${account.company_id}:${account.company_name}`).join('|');
  const accountsRef = React.useRef(accounts);
  accountsRef.current = accounts;

  React.useEffect(() => {
    let cancel = false;
    const load = async () => {
      const rows: PortfolioCalendarEvent[] = [];
      const saved: Record<string, { date: string; skipped?: boolean }> = {};
      const targets = accountsRef.current.length ? accountsRef.current : [{ company_id: 0, company_name: '' }];
      await Promise.all(targets.flatMap((account) => Object.values(COMPLIANCE_CONSOLES).map(async (definition) => {
        const storageKey = account.company_id ? `${definition.storageKey}:${account.company_id}` : definition.storageKey;
        let events = loadComplianceConsoleState(storageKey).calendarEvents || [];
        if (accessToken && apiBase && account.company_id) {
          try {
            const response = await fetch(
              `${apiBase}/api/v1/compliance-consoles/${encodeURIComponent(definition.kind)}?company_id=${encodeURIComponent(String(account.company_id))}`,
              { headers: { Authorization: `Bearer ${accessToken}` } },
            );
            if (response.ok) {
              const body = await response.json() as { state?: { calendarEvents?: unknown } };
              events = parseCalendarEvents(body.state?.calendarEvents);
            }
          } catch {
            // Keep the dates already saved in this browser.
          }
        }
        const source = account.company_name ? `${account.company_name} · ${definition.title}` : definition.title;
        events.forEach((event) => {
          const key = `${account.company_id}:${definition.kind}:${event.id}`;
          const previous = saved[key];
          if (!previous || (event.date && !previous.date)) {
            saved[key] = { date: event.date, skipped: event.skipped };
          }
          if (event.date && !event.skipped) {
            rows.push({ source, event: { ...event, id: `${account.company_id}-${definition.kind}-${event.id}` } });
          }
        });
      })));
      if (!cancel) {
        setPortfolio(rows);
        setSavedByKey(saved);
      }
    };
    void load();
    return () => {
      cancel = true;
    };
  }, [accessToken, accountKey, apiBase]);

  const listRows: CalendarListRow[] = (accounts.length ? accounts : [{ company_id: 0, company_name: '' }]).flatMap((account) => (
    Object.values(COMPLIANCE_CONSOLES).flatMap((definition) => (
      calendarSuggestionsFor(definition.kind).map((suggestion) => {
        const saved = savedByKey[`${account.company_id}:${definition.kind}:${suggestion.id}`];
        let due = '';
        if (saved?.skipped) {
          due = language === 'es' ? 'No este año' : 'Not this year';
        } else if (saved?.date) {
          due = saved.date;
        }
        const type = account.company_name ? `${account.company_name} · ${definition.title}` : definition.title;
        return {
          id: `${account.company_id}:${definition.kind}:${suggestion.id}`,
          task: suggestion.title[language],
          type,
          frequency: frequencyLabel(suggestion.frequency, language),
          due,
        };
      })
    ))
  ));

  return (
    <div className="dashboard-section-card">
      <p className="compliance-calendar-note">
        {language === 'es'
          ? 'Estas son las fechas guardadas en cada función de cumplimiento. Para cambiar una fecha, ábrala en esa función.'
          : 'These are the dates saved on each compliance feature. Open that feature to change a date.'}
      </p>
      <ComplianceCalendar
        language={language}
        events={[]}
        suggestions={[]}
        portfolio={portfolio}
        listRows={listRows}
        heading={language === 'es' ? 'Calendario de cumplimiento' : 'Compliance Calendar'}
        showEditor={false}
        published={PUBLISHED_DEADLINES.flatMap((row) => ([
          {
            id: `${row.id}-prepare`,
            title: language === 'es' ? `Preparar: ${row.title.es}` : `Prepare: ${row.title.en}`,
            date: row.prepareBy,
            source: row.source[language],
            kind: 'prepare' as const,
          },
          {
            id: `${row.id}-file`,
            title: row.title[language],
            date: row.date,
            source: row.source[language],
            kind: 'file' as const,
          },
        ]))}
        onSave={() => undefined}
      />
    </div>
  );
};

export default CompliancePortfolioPage;
